<template>
  <box>
    <template #header>
      <h2 class="header-title">{{ t('Notificacoes de Retirada') }}</h2>
    </template>

    <div v-if="notifications.length === 0" class="empty-state">
      {{ t('Nenhuma notificacao de retirada enviada') }}
    </div>

    <div v-else class="notifications-container">
      <div
        v-for="notif in notifications"
        :key="notif.so_opin_id"
        class="notification-row"
      >
        <!-- Channel icon -->
        <div class="channel-icon-wrapper">
          <i :class="channelIcon(notif.so_opin_channel)" class="channel-icon" />
        </div>

        <!-- Notification details -->
        <div class="notification-details">
          <div class="notification-header">
            <span class="channel-label">{{ channelLabel(notif.so_opin_channel) }}</span>
            <span v-if="notif.so_opin_sent_by_name" class="sender-name">
              — {{ notif.so_opin_sent_by_name }}
            </span>
          </div>

          <div v-if="notif.so_opin_notes" class="notification-notes">
            {{ notif.so_opin_notes }}
          </div>

          <div class="notification-timestamp">
            {{ formatDateTime(new Date(notif.so_opin_created_at)) }}
          </div>
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../composables/useDateFormatter';
import type { Orders_PickupNotificationRow } from '../../contracts/Orders_PickupNotificationRow';

const { t } = useI18n();
const { formatDateTime } = useDateFormatter();

interface Props {
  notifications?: Orders_PickupNotificationRow[];
}

withDefaults(defineProps<Props>(), {
  notifications: () => [],
});

function channelIcon(channel: string): string {
  switch (channel) {
    case 'whatsapp':
      return 'pi pi-whatsapp';
    case 'instagram':
      return 'pi pi-instagram';
    case 'manual':
      return 'pi pi-phone';
    default:
      return 'pi pi-bell';
  }
}

function channelLabel(channel: string): string {
  switch (channel) {
    case 'whatsapp':
      return 'WhatsApp';
    case 'instagram':
      return 'Instagram';
    case 'manual':
      return t('Ligacao');
    default:
      return t('Outro');
  }
}
</script>

<style scoped>
.header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.empty-state {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0;
}

.notifications-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notification-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-surface-border);
}

.notification-row:last-child {
  border-bottom: none;
}

.channel-icon-wrapper {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: var(--p-blue-50);
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-icon {
  color: var(--p-blue-500);
  font-size: 0.875rem;
}

.notification-details {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.channel-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.sender-name {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.notification-notes {
  font-size: 0.75rem;
  color: var(--p-text-color);
  margin-top: 0.25rem;
}

.notification-timestamp {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-top: 0.25rem;
}
</style>
